import { extractUrlIngredients } from '../utils';

export const findNodes = (predicate, nodes, nodeId) => {
  const currentNode = nodes?.[nodeId];
  if (!currentNode) {
    return [];
  }
  const fromChildren = currentNode.nodes
    ? currentNode.nodes.flatMap(childId => findNodes(predicate, nodes, childId))
    : [];
  return [...(predicate(currentNode) ? [currentNode] : []), ...fromChildren];
};

const findNodesBySubTypeImpl = (subType, nodes, nodeId) =>
  findNodes(node => node?.subType === subType, nodes, nodeId);

/** Ramda-curry-compatible: f(a,b,c) | f(a)(b,c) | f(a,b)(c) | f(a)(b)(c) */
export const findNodesBySubType = (...args) => {
  const [subType, nodes, nodeId] = args;
  if (args.length >= 3) {
    return findNodesBySubTypeImpl(subType, nodes, nodeId);
  }
  if (args.length === 2) {
    return nid => findNodesBySubTypeImpl(subType, nodes, nid);
  }
  return function partialNodes(nodes2, nodeId2) {
    if (arguments.length >= 2) {
      return findNodesBySubTypeImpl(subType, nodes2, nodeId2);
    }
    return nid => findNodesBySubTypeImpl(subType, nodes2, nid);
  };
};

export const findGroupNodes = findNodesBySubType('group');
export const findHeaderNodes = findNodesBySubType('header');
export const findHeaderNode = (nodes, nodeId) =>
  findHeaderNodes(nodes, nodeId)?.[0];
export const findLinkNodes = findNodesBySubType('link');

export const extractImageProps = image => {
  if (!image) return null;

  if (image.squarish) {
    return { inlineImage: image.squarish };
  }
  if (image.portrait) {
    return { verticalImage: image.portrait };
  }
  if (image.landscape) {
    return { horizontalImage: image.landscape };
  }
  return null;
};
// data object skimmer function
export const extractNodeProps = (node, { inLocalNav = false } = {}) => {
  const { destination, label, image } = node?.properties ?? {};
  const imageProps = inLocalNav && extractImageProps(image);
  const { type, openInNewTab } = destination ?? {};
  if (!node) return {};

  const hashKey = inLocalNav ? node?.analytics?.hashKey : undefined;

  return {
    ...(node?.id !== undefined && { id: node?.id }),
    ...(label && { label }),
    ...(destination &&
      (destination.url
        ? {
            url: destination?.url,
          }
        : {
            ...extractUrlIngredients(destination ?? {}),
          })),
    ...(type && { destinationType: type }),
    ...(openInNewTab && { openInNewTab }),
    ...(imageProps && { ...imageProps }),
    ...(hashKey && { hashKey }),
  };
};

export { getLocalNavItems, getLocalNavTitle } from './localNav';
export { getTeamSelectorNodes } from './teamSelector';
export {
  getImageOverride,
  getFocalPoint,
  getAssetsAspectRatio,
} from './imageHelpers';
export { getMerchMenuItems } from './merchMenu';
export { sanitizeText, sanitizeRichText } from './textHelpers';
export { smartTextCut } from './smartTextCut';
export {
  getProductCarouselTranslations,
  getPaginationTranslations,
} from './translation';
export {
  getPDPType,
  getUrlIngredients,
  getPDPIngredients,
  pickUrlByIngredients,
  getPDPUrl,
  getPDPURL,
  extractIngredientsFromList,
  mergeLinkWithIngredients,
} from './url';
export { extractBuyingIngredientsFromList } from './buyingTools';
export {
  mergeSectionHeadlineTitle,
  mergeCarouselProperties,
  getCarouselWithTitle,
  prepareDynamicContentCarousel,
} from './carouselHelper';
export {
  prepareGenericCardData,
  getTitle,
  getContainerType,
  getActionButtons,
  prepareActionButton,
  getPageType,
  isArticle,
  getGlyph,
  getPageThread,
  getProfilesForRole,
  getTextLocation,
  formatArticleDate,
  getCommonProps,
  getButtonTitle,
  prepareTextData,
} from './commonHelpers';

export {
  mergeSnkrsCarouselProperties,
  getSnkrsProductStatus,
} from './snkrsDropsHelpers';

export { getBaseFilters } from './feedApiHelpers';
export { prepareRoleForRender, prepareBodyText } from './bylineHelpers';

export {
  isMemberExlcusiveProduct,
  isNikeByYouProduct,
  formatPrice,
} from './productHelpers';
